import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  GridOn as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { payrollApi } from '../../../api/payrollApi';
import { PayslipDetailDto, PayslipDto } from '../../../types/payrollTypes';
import PayslipTemplateRenderer, { buildPayslipPrintHtml } from '../components/PayslipTemplateRenderer';

const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

type SortKey = 'employeeName' | 'employeeCode' | 'period' | 'generatedAt';
type SortDirection = 'asc' | 'desc';

const exportColumns = ['#', 'Employee', 'Code', 'Month', 'Year', 'Generated At'];

const PayslipsPage: React.FC = () => {
  const [payslips, setPayslips] = useState<PayslipDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<PayslipDetailDto | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('generatedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadPayslips = async () => {
      setLoading(true);
      try {
        setPayslips(await payrollApi.getPayslips());
      } catch {
        toast.error('Failed to load payslips');
      } finally {
        setLoading(false);
      }
    };
    void loadPayslips();
  }, []);

  const handleView = async (id: string) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      setDetail(await payrollApi.getPayslipDetail(id));
    } catch {
      toast.error('Failed to load payslip detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(buildPayslipPrintHtml(printRef.current.innerHTML));
    printWindow.document.close();
    printWindow.print();
  };

  const years = useMemo(
    () => Array.from(new Set(payslips.map((slip) => slip.year))).sort((a, b) => b - a),
    [payslips]
  );

  const filteredPayslips = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return payslips.filter((slip) => {
      const matchesSearch = !search
        || slip.employeeName.toLowerCase().includes(search)
        || (slip.employeeCode ?? '').toLowerCase().includes(search);
      const matchesMonth = !monthFilter || slip.month === Number(monthFilter);
      const matchesYear = !yearFilter || slip.year === Number(yearFilter);
      return matchesSearch && matchesMonth && matchesYear;
    });
  }, [monthFilter, payslips, searchTerm, yearFilter]);

  const sortedPayslips = useMemo(() => {
    const getSortValue = (slip: PayslipDto): string | number => {
      if (sortKey === 'period') return slip.year * 100 + slip.month;
      if (sortKey === 'generatedAt') return new Date(slip.generatedAt).getTime();
      if (sortKey === 'employeeCode') return slip.employeeCode ?? '';
      return slip.employeeName || slip.employeeCode || '';
    };

    return [...filteredPayslips].sort((a, b) => {
      const first = getSortValue(a);
      const second = getSortValue(b);
      const result = typeof first === 'number' && typeof second === 'number'
        ? first - second
        : String(first).localeCompare(String(second), undefined, { sensitivity: 'base' });
      return sortDirection === 'asc' ? result : -result;
    });
  }, [filteredPayslips, sortDirection, sortKey]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'generatedAt' || key === 'period' ? 'desc' : 'asc');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setMonthFilter('');
    setYearFilter('');
  };

  const getExportRows = () => sortedPayslips.map((slip, index) => ({
    '#': index + 1,
    Employee: slip.employeeName || slip.employeeCode || '-',
    Code: slip.employeeCode || '-',
    Month: monthNames[slip.month] || String(slip.month),
    Year: slip.year,
    'Generated At': new Date(slip.generatedAt).toLocaleDateString('en-IN'),
  }));

  const getExportFileName = (extension: 'xlsx' | 'pdf') => {
    const period = monthFilter || yearFilter
      ? `${monthFilter ? monthNames[Number(monthFilter)] : 'AllMonths'}_${yearFilter || 'AllYears'}`
      : 'All';
    return `Payslips_${period}_${new Date().toISOString().slice(0, 10)}.${extension}`;
  };

  const handleExportExcel = () => {
    const rows = getExportRows();
    if (rows.length === 0) {
      toast.error('No payslip data available to export');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(rows, { header: exportColumns });
    ws['!cols'] = exportColumns.map((key) => ({
      wch: Math.max(key.length, ...rows.map((row) => String((row as Record<string, unknown>)[key] ?? '').length)) + 3,
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payslips');
    const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbOut], { type: 'application/octet-stream' }), getExportFileName('xlsx'));
  };

  const handleExportPdf = () => {
    const rows = getExportRows();
    if (rows.length === 0) {
      toast.error('No payslip data available to export');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text('Payslips', 14, 15);
    doc.setFontSize(10);
    doc.text(`Total Records: ${rows.length}`, 14, 22);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, 14, 28);
    autoTable(doc, {
      startY: 34,
      head: [exportColumns],
      body: rows.map((row) => exportColumns.map((column) => String((row as Record<string, unknown>)[column] ?? ''))),
      styles: { fontSize: 8, cellPadding: 2, lineColor: [227, 231, 240], lineWidth: 0.1 },
      headStyles: { fillColor: [79, 99, 215], textColor: 255, fontStyle: 'bold', halign: 'center' },
      alternateRowStyles: { fillColor: [244, 247, 255] },
    });
    doc.save(getExportFileName('pdf'));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>Payslips</Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} mb={2}>
            <TextField size="small" label="Search employee" placeholder="Name or code" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} sx={{ minWidth: { md: 280 } }} />
            <TextField select size="small" label="Month" value={monthFilter} onChange={(event) => setMonthFilter(event.target.value)} sx={{ minWidth: { md: 160 } }}>
              <MenuItem value="">All Months</MenuItem>
              {monthNames.slice(1).map((month, index) => <MenuItem key={month} value={String(index + 1)}>{month}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Year" value={yearFilter} onChange={(event) => setYearFilter(event.target.value)} sx={{ minWidth: { md: 140 } }}>
              <MenuItem value="">All Years</MenuItem>
              {years.map((year) => <MenuItem key={year} value={String(year)}>{year}</MenuItem>)}
            </TextField>
            <Button variant="outlined" onClick={clearFilters} sx={{ textTransform: 'none' }}>
              Clear
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" startIcon={<ExcelIcon />} onClick={handleExportExcel} sx={{ textTransform: 'none' }}>
              Excel
            </Button>
            <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleExportPdf} sx={{ textTransform: 'none' }}>
              Pdf
            </Button>
            <Chip label={`Total: ${sortedPayslips.length}`} size="small" color="primary" variant="outlined" />
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><TableSortLabel active={sortKey === 'employeeName'} direction={sortKey === 'employeeName' ? sortDirection : 'asc'} onClick={() => handleSort('employeeName')}>Employee</TableSortLabel></TableCell>
                  <TableCell><TableSortLabel active={sortKey === 'employeeCode'} direction={sortKey === 'employeeCode' ? sortDirection : 'asc'} onClick={() => handleSort('employeeCode')}>Code</TableSortLabel></TableCell>
                  <TableCell><TableSortLabel active={sortKey === 'period'} direction={sortKey === 'period' ? sortDirection : 'desc'} onClick={() => handleSort('period')}>Month/Year</TableSortLabel></TableCell>
                  <TableCell><TableSortLabel active={sortKey === 'generatedAt'} direction={sortKey === 'generatedAt' ? sortDirection : 'desc'} onClick={() => handleSort('generatedAt')}>Generated At</TableSortLabel></TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPayslips.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">No payslips found</TableCell></TableRow>
                ) : sortedPayslips.map((slip) => (
                  <TableRow key={slip.id}>
                    <TableCell><Typography fontWeight={600}>{slip.employeeName || slip.employeeCode || '-'}</Typography></TableCell>
                    <TableCell>{slip.employeeCode || '-'}</TableCell>
                    <TableCell>{`${monthNames[slip.month] || slip.month} ${slip.year}`}</TableCell>
                    <TableCell>{new Date(slip.generatedAt).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Payslip">
                        <IconButton size="small" color="primary" onClick={() => handleView(slip.id)}><ViewIcon /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : detail ? (
            <Box ref={printRef}><PayslipTemplateRenderer detail={detail} /></Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {detail && <Button onClick={handlePrint} startIcon={<PrintIcon />} variant="outlined" sx={{ textTransform: 'none' }}>
            Print
          </Button>}
          <Button onClick={() => setDetailOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayslipsPage;
